import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormGroupDirective,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import {
  CouponEnum,
  PayoffEnum,
  PayoffScheduleEnum,
  PayoffScheduleObservationEnum,
  ScheduleEnum,
  ScheduleType,
} from 'src/app/product-capture/model/enums/product-capture.enums';

import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { pairwise, takeUntil, tap } from 'rxjs/operators';
import { ScheduleRowColumnVisibility } from 'src/app/product-capture/model/classes/schedule-row-column-visibility';
import { EditableDate } from 'src/app/product-capture/model/interfaces/editable-date.interface';
import {
  PayoffScheduleObservation,
  RateResetDatesDetail,
} from 'src/app/product-capture/model/interfaces/payoff-schedule-observation.interface';
import { DEFERRED_CALL_EVENT_TYPE } from 'src/app/product-capture/model/interfaces/payoff-schedule.interface';
import { BarrierLevelPipe } from './barrier-level.pipe';
import { RedemptionAmountPipe } from './redemption-amount.pipe';
import { COUPON_TYPE } from '../../../../model/enums/coupon-type.enum';
import { BarrierType } from '../../../../../static-data/models/consts/barrier-types.const';

const displayNone = 'displayNone';
@Component({
  selector: 'app-schedule-row',
  templateUrl: './schedule-row.component.html',
  styleUrls: ['./schedule-row.component.scss'],
})
export class ScheduleRowComponent implements OnInit, OnDestroy {
  @Input() scheduleForm: UntypedFormGroup;
  @Input() featureForm: UntypedFormGroup; // This is either the payoff > coupo / callability / participation form
  @Input() groupName: number;
  @Input() columnVisibility: ScheduleRowColumnVisibility;
  @Input() templateSettings: any;
  @Input() isHidden: boolean;
  @Input() type: ScheduleType;

  @Output()
  rowUpdated = new EventEmitter();

  editForm: UntypedFormGroup = null;
  dirtyColumns = [];
  isLoading = true;
  isEditable = true;
  destroy$ = new Subject<boolean>();

  isEditing$ = new BehaviorSubject<boolean>(false);

  @Input()
  barrierObservable$: Observable<any>;

  schedule: FormArray;

  barrierTypeChanged = false;

  constructor(
    private fb: FormBuilder,
    public rootFormGroup: FormGroupDirective
  ) {}

  ngOnInit(): void {
    this.schedule = this.scheduleForm.get(CouponEnum.schedule) as FormArray;

    this.editForm = this.fb.group({
      period: [],
      unadjustedPaymentDate: [],
      adjustedPaymentDate: [],
      type: [],
      accrualPeriodStart: [],
      accrualPeriodEnd: [],
      accrualPeriodInclusion: [],
      dayCountFraction: [],
      rateDeterminationDate: [],
      observations: new UntypedFormArray([]),
      barrierLevel: [],
      redemptionAmountPayable_relative: [],
      callNoticeDate: [],
      lowerLevel: [],
      upperLevel: [],
    });

    this.resetArray();

    this.barrierObservable$
      .pipe(
        takeUntil(this.destroy$),
        tap((barrier) => {
          setTimeout(() => {
            const isEditing = this.isEditing$.value;
            this.isEditing$.next(!isEditing);
          });
          return barrier;
        }),
        pairwise()
      )
      .subscribe(([oldBarrier, currBarrier]) => {
        setTimeout(() => {
          const barrierLevelControl = this.editForm.get('barrierLevel');
          const upperBarrierLevelControl = this.editForm.get('upperLevel');
          const lowerBarrierLevelControl = this.editForm.get('lowerLevel');
          const redemptionAmountControl = this.editForm.get(
            'redemptionAmountPayable_relative'
          );
          const previousBarrierLevel = barrierLevelControl.value?.value;
          const previousUpperBarrierLevel = barrierLevelControl.value?.value;
          const previousLowerBarrierLevel = barrierLevelControl.value?.value;

          if (
            previousBarrierLevel === 'Deferred' ||
            oldBarrier !== currBarrier
          ) {
            barrierLevelControl.setValue({
              value: null,
              minValue: null,
              maxValue: null,
              indicativeValue: null,
            });
            upperBarrierLevelControl.setValue({
              value: null,
              minValue: null,
              maxValue: null,
              indicativeValue: null,
            });
            lowerBarrierLevelControl.setValue({
              value: null,
              minValue: null,
              maxValue: null,
              indicativeValue: null,
            });
          }

          const barrierLevel = new BarrierLevelPipe().transform(
            [],
            this.featureForm,
            this.editForm,
            this.groupName,
            this.type
          );
          barrierLevelControl.patchValue({
            value: barrierLevel,
            indicativeValue: null,
            maxValue: null,
            minValue: null,
          });

          const upperBarrierLevel = new BarrierLevelPipe().transform(
            [],
            this.featureForm,
            this.editForm,
            this.groupName,
            this.type,
            'upper'
          );
          upperBarrierLevelControl.patchValue({
            value: upperBarrierLevel,
            indicativeValue: null,
            maxValue: null,
            minValue: null,
          });

          const lowerBarrierLevel = new BarrierLevelPipe().transform(
            [],
            this.featureForm,
            this.editForm,
            this.groupName,
            this.type,
            'lower'
          );
          lowerBarrierLevelControl.patchValue({
            value: lowerBarrierLevel,
            indicativeValue: null,
            maxValue: null,
            minValue: null,
          });

          const redemptionAmount = new RedemptionAmountPipe().transform(
            [],
            this.featureForm,
            this.editForm,
            this.groupName,
            this.type
          );
          redemptionAmountControl.patchValue({
            value: redemptionAmount,
          });

          if (
            (oldBarrier !== currBarrier ||
              previousBarrierLevel !== barrierLevel ||
              previousUpperBarrierLevel !== upperBarrierLevel ||
              previousLowerBarrierLevel !== lowerBarrierLevel) &&
            this.groupInArray
          ) {
            this.groupInArray.patchValue(this.editForm.value);
            this.rowUpdated.emit();
          }

          this.barrierTypeChanged = true;
        });
      });
  }

  editRow(displayRow: Element, editRow: Element): void {
    this.isEditing$.next(true);

    displayRow.classList.add(displayNone);
    editRow.classList.remove(displayNone);
  }

  cancelRowEdit(displayRow: Element, editRow: Element): void {
    displayRow.classList.remove(displayNone);
    editRow.classList.add(displayNone);
    this.resetArray();
    this.isEditing$.next(false);
  }

  doneRowEdit(displayRow: Element, editRow: Element): void {
    displayRow.classList.remove(displayNone);
    editRow.classList.add(displayNone);

    Object.keys(this.editForm.controls).forEach((control) => {
      const editFormValue = this.editForm.get(control)?.value;
      const groupInArrayValue = this.groupInArray.get(control)?.value;

      if (Array.isArray(editFormValue) && Array.isArray(groupInArrayValue)) {
        // We are iterating over the observations array which is an array of objects
        editFormValue.forEach((item, index) => {
          const oldValue = groupInArrayValue[index] ?? {};
          const newValue = item ?? {};
          Object.keys(newValue)
            .filter(
              (key) =>
                key !== PayoffScheduleEnum.rateResetDatesDetail &&
                key != PayoffScheduleEnum.rateDeterminationDates
            ) // Filtering out the arrays that are not editable
            .forEach((key) => {
              if (this.isFieldModified(oldValue[key], newValue[key])) {
                newValue[key] = this.reconcileEditableDate(
                  oldValue[key],
                  newValue[key]
                );

                this.dirtyColumns = [...new Set(this.dirtyColumns.concat(key))];
              }
            });
        });
      } else if (this.isFieldModified(groupInArrayValue, editFormValue)) {
        let reconciled = editFormValue;
        if (
          control !== PayoffScheduleObservationEnum.barrierLevel &&
          control !== PayoffScheduleObservationEnum.upperLevel &&
          control !== PayoffScheduleObservationEnum.lowerLevel
        ) {
          reconciled = this.reconcileEditableDate(
            groupInArrayValue,
            editFormValue
          );
        }

        this.editForm.get(control).patchValue(reconciled);

        if (control === PayoffScheduleEnum.rateDeterminationDate) {
          // If rateDeterminationDate has been modified, we need to update the last element
          // in the rateDeterminationDates array
          const observations = this.editForm.get(
            PayoffScheduleEnum.observations
          ) as UntypedFormArray;
          const lastObservation = observations.at(observations.length - 1);
          const rateDeterminationDates = (
            lastObservation.get(
              PayoffScheduleEnum.rateDeterminationDates
            ) as UntypedFormControl
          )?.value;
          if (rateDeterminationDates?.length) {
            const newArray = [
              ...rateDeterminationDates.slice(0, -1),
              reconciled,
            ];
            lastObservation
              .get(PayoffScheduleEnum.rateDeterminationDates)
              .patchValue(newArray);
          }
        }

        this.dirtyColumns = [...new Set(this.dirtyColumns.concat(control))];
      }
    });

    this.groupInArray.patchValue(this.editForm.value);

    this.rowUpdated.emit();
    this.isEditing$.next(false);
  }

  private isFieldModified(oldValue: any, newValue: any): boolean {
    if (newValue?.userEdited) {
      return true;
    }

    if (
      Object.keys(newValue ?? {}).includes('year') ||
      Object.keys(oldValue ?? {}).includes('year')
    ) {
      return this.isDateFieldModified(oldValue, newValue);
    }

    if (
      Object.keys(newValue ?? {}).includes('indicativeValue') ||
      Object.keys(oldValue ?? {}).includes('indicativeValue')
    ) {
      return this.isValueFieldModified(oldValue, newValue);
    }

    return newValue !== oldValue;
  }

  private isDateFieldModified(oldValue: any, newValue: any): boolean {
    return (
      newValue?.year !== oldValue?.year ||
      newValue?.month !== oldValue?.month ||
      newValue?.day !== oldValue?.day
    );
  }

  private isValueFieldModified(oldValue: any, newValue: any): boolean {
    return (
      newValue?.value !== oldValue?.value ||
      newValue?.indicativeValue !== oldValue?.indicativeValue ||
      newValue?.maxValue !== oldValue?.maxValue ||
      newValue?.minValue !== oldValue?.minValue
    );
  }

  private reconcileEditableDate(
    oldItem: EditableDate,
    newItem: (EditableDate & { userEdited: NgbDate }) | null
  ): EditableDate {
    if (newItem?.userEdited) {
      if (!this.isDateFieldModified(oldItem.original, newItem.userEdited)) {
        return {
          original: newItem.original,
          edited: null,
        };
      } else {
        // Merge the userEdited property into the edited property on the edit form
        return {
          original: newItem.original,
          edited: newItem.userEdited,
        };
      }
    }
  }

  private resetArray(): void {
    const value = this.schedule.get([this.groupName])?.value;

    if (!value) {
      return;
    }

    if (!this.barrierTypeChanged) {
      this.editForm.patchValue(value);
    }

    if (value.observations?.length) {
      this.editForm.setControl(
        PayoffScheduleEnum.observations,
        this.fb.array([])
      );
      value.observations.forEach((observation: PayoffScheduleObservation) => {
        this.addObservation(observation);
      });
    }

    this.isLoading = false;
    this.isEditable =
      this.groupInArray.get(PayoffScheduleEnum.type).value !==
        DEFERRED_CALL_EVENT_TYPE ||
      !(
        (this.featureForm.get('type')?.value === COUPON_TYPE.RANGE_ACCRUAL ||
          this.featureForm.get('type')?.value ===
            COUPON_TYPE.FIXED_TO_RANGE_ACCRUAL) &&
        this.barrierType.value === BarrierType.CUSTOM
      );
  }

  addObservation(observation: PayoffScheduleObservation = null): void {
    const observations = this.editForm.get(
      PayoffScheduleEnum.observations
    ) as UntypedFormArray;
    const newObservation: FormGroup = this.fb.group({
      unadjustedObsEndDate: [],
      unadjustedObsStartDate: [],
      adjustedObsEndDate: [],
      assetId: [],
      barrierObsDatesInclusion: [],
      adjustedBarrierObsDates: [],
      adjustedBarrierFixingDates: [],
      unadjustedInitialFixingDates: [],
      adjustedInitialFixingDates: [],
      unadjustedFinalFixingDates: [],
      adjustedFinalFixingDates: [],
      rateDeterminationDate: [],
      rateLastResetDateForFixing: [],
      rateCalculationDates: [],
      rateCalculationDateCounts: [],
      rateResetDates: [],
      rateResetDatesDetail: this.fb.array<RateResetDatesDetail>([]),
      rateDeterminationDates: [],
    });

    if (observation) {
      newObservation.patchValue(observation);
      if (observation.rateResetDatesDetail?.length) {
        const rateResetDatesDetail = newObservation.get(
          PayoffScheduleEnum.rateResetDatesDetail
        ) as FormArray;
        observation.rateResetDatesDetail.forEach((detail) => {
          rateResetDatesDetail.push(
            this.fb.group(detail ? { ...detail } : null)
          );
        });
      }
    }

    observations.push(newObservation);
  }

  isParticipation(type: PayoffEnum): boolean {
    return type === PayoffEnum.participation;
  }

  get groupInArray(): FormGroup {
    return this.schedule.at(this.groupName) as FormGroup;
  }

  get isCoupon(): boolean {
    return this.scheduleType === PayoffEnum.coupon;
  }

  get rateDeterminationDateValue(): NgbDate {
    return this.groupInArray.get(PayoffScheduleEnum.rateDeterminationDate)
      .value;
  }

  get rateDeterminationDate(): string | NgbDate {
    const observations = this.groupInArray.get(
      PayoffScheduleEnum.observations
    ) as FormArray;
    const rateDeterminationDate = observations.value?.length
      ? observations.value[0][PayoffScheduleEnum.rateDeterminationDate]
      : null;

    return this.rateDeterminationDateValue?.day
      ? this.rateDeterminationDateValue
      : rateDeterminationDate;
  }

  get unadjustedObsEndDate(): string | NgbDate {
    const observations = this.groupInArray.get(
      PayoffScheduleEnum.observations
    ) as FormArray;
    return observations.value?.length
      ? observations.value[0][PayoffScheduleEnum.unadjustedObsEndDate]
      : null;
  }

  get adjustedObsEndDate(): string | NgbDate {
    const observations = this.groupInArray.get(
      PayoffScheduleEnum.observations
    ) as FormArray;
    return observations.value?.length
      ? observations.value[0][PayoffScheduleEnum.adjustedObsEndDate]
      : null;
  }

  get callNoticeDate(): string | NgbDate {
    return this.groupInArray.get(PayoffScheduleEnum.callNoticeDate).value;
  }

  get accrualPeriodStart(): string | NgbDate {
    return this.groupInArray.get(PayoffScheduleEnum.accrualPeriodStart).value;
  }

  get accrualPeriodEnd(): string | NgbDate {
    return this.groupInArray.get(PayoffScheduleEnum.accrualPeriodEnd).value;
  }

  get adjustedPaymentDate(): string | NgbDate {
    return this.groupInArray.get(PayoffScheduleEnum.adjustedPaymentDate).value;
  }

  get unadjustedPaymentDate(): string | NgbDate {
    return this.groupInArray.get(PayoffScheduleEnum.unadjustedPaymentDate)
      .value;
  }

  get observationsFirstItem(): FormGroup {
    return (this.groupInArray.get(PayoffScheduleEnum.observations) as FormArray)
      .at[0];
  }

  get observationsValue(): FormGroup {
    return this.groupInArray.get(PayoffScheduleEnum.observations).value;
  }

  get scheduleType(): string {
    return this.scheduleForm.get(ScheduleEnum.scheduleType).value;
  }

  get redemptionAmountPayable_relative(): FormGroup {
    return this.groupInArray.get('redemptionAmountPayable_relative').value;
  }

  get lowerLevel(): FormGroup {
    return this.groupInArray.get('lowerLevel').value;
  }

  get upperLevel(): FormGroup {
    return this.groupInArray.get('upperLevel').value;
  }

  get rangeAccrualRate(): FormGroup {
    return this.featureForm.get('rangeAccrualRate').value;
  }

  get barrierType(): FormGroup {
    return this.featureForm.get('barrier.barrierType').value;
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
